import React, { Fragment, useEffect, useLayoutEffect, useRef } from "react";
import { Combobox, Dialog, Portal, Transition } from "@headlessui/react";
import { SearchIcon } from "@heroicons/react/outline";
import { cn } from "./cn";
import Button from './Button';
import Kbd from './Kbd';
import FloatingPanel from './FloatingPanel';

/**
 * Bun command palette (⌘K). Spec: Component Library "Menus, command palette
 * & tooltips". Floating card near the top of the viewport; grouped results;
 * arrow keys + Enter come from headlessui's Combobox.
 *
 *   <CommandPalette open={open} onClose={...} query={q} onQueryChange={setQ}
 *     onSelect={(item) => ...} placeholder="Find a client">
 *     <CommandPalette.Group label="Clients">
 *       <CommandPalette.Item value={patel} icon={<Avatar size="xs" ... />} hint="↵ open">
 *         Maya & Dev <CommandPalette.Match>Pat</CommandPalette.Match>el
 *         <CommandPalette.Meta>· $1.24M</CommandPalette.Meta>
 *       </CommandPalette.Item>
 *     </CommandPalette.Group>
 *   </CommandPalette>
 *
 * The caller owns filtering: pass the query text back in and render only
 * matching items, wrapping matched substrings in <CommandPalette.Match>.
 * Null selections from empty or disabled rows do not invoke `onSelect`.
 * Use `portal` for detail dialogs so they retain this dialog's nesting context
 * without becoming combobox options.
 */
export default function CommandPalette({
  open,
  variant,
  anchorRef,
  portalTarget,
  keyboardTargetRef,
  inputLeading,
  onClose,
  query = "",
  onQueryChange,
  onSelect,
  placeholder = "Search",
  ariaLabel = "Search commands",
  value = null,
  width = 560,
  mobileSheet = false,
  mobileFullscreen = false,
  title,
  subtitle,
  portal,
  footer,
  selectOnTab = true,
  children,
}) {
  const inputRef = useRef(null);
  if (variant === 'anchored') return open ? <Combobox value={value} onChange={item => item != null && onSelect?.(item)}>
    {({ open: resultsOpen }) => <AnchoredResults resultsOpen={resultsOpen} inputRef={inputRef} anchorRef={anchorRef}
      portalTarget={portalTarget} keyboardTargetRef={keyboardTargetRef} inputLeading={inputLeading}
      onClose={onClose} query={query} onQueryChange={onQueryChange} placeholder={placeholder}
      ariaLabel={ariaLabel} width={width} selectOnTab={selectOnTab}>{children}</AnchoredResults>}
  </Combobox> : null;
  return (
    <Transition show={!!open} as={Fragment} afterLeave={() => onQueryChange && onQueryChange("")}>
      <Dialog onClose={onClose} initialFocus={inputRef} aria-label={ariaLabel} className="bun relative z-50 font-instrument">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[color:var(--bun-scrim)]" aria-hidden="true" />
        </Transition.Child>
        <div className={cn('fixed inset-0 overflow-y-auto', mobileFullscreen ? 'sm:p-6 sm:pt-[15vh]' : mobileSheet ? 'flex items-end sm:block sm:p-6 sm:pt-[15vh]' : 'p-6 pt-[15vh]')}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 -translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Panel
              style={{ "--palette-width": width + "px" }}
              className={cn('mx-auto max-w-full bg-bun-surface text-bun-text border border-bun-line shadow-bun-modal overflow-hidden', mobileFullscreen ? 'flex h-[100dvh] w-full flex-col border-0 pt-[env(safe-area-inset-top)] sm:h-auto sm:w-[var(--palette-width)] sm:rounded-bun-xl sm:border sm:pt-0' : mobileSheet ? 'flex w-[var(--palette-width)] max-h-[90dvh] flex-col rounded-t-bun-xl sm:rounded-bun-xl' : 'w-[var(--palette-width)] rounded-bun-xl')}
            >
              {mobileSheet && <div aria-hidden="true" className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-bun-track-off sm:hidden" />}
              {title && <div className="flex shrink-0 items-center gap-3 px-[18px] py-3"><div className="min-w-0 flex-1"><Dialog.Title className="m-0 font-instrument text-bun-heading font-semibold leading-snug tracking-normal">{title}</Dialog.Title>{subtitle && <p className="m-0 text-bun-meta text-bun-stone">{subtitle}</p>}</div><Button variant="ghost" onClick={onClose}>Done</Button></div>}
              <Combobox value={value} onChange={(item) => item != null && onSelect?.(item)}>
                <div className="flex shrink-0 items-center gap-[10px] px-4 py-[13px] border-b border-bun-line-soft">
                  <SearchIcon className="w-4 h-4 shrink-0 text-bun-stone" />
                  <Combobox.Input
                    ref={inputRef}
                    autoFocus
                    aria-label={placeholder}
                    className="flex-1 min-w-0 border-0 bg-transparent p-0 text-[14px] font-book text-bun-text placeholder:text-bun-faint caret-bun-blue focus:outline-none focus:ring-0"
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => onQueryChange && onQueryChange(e.target.value)}
                    onKeyDownCapture={(event) => {
                      if (event.key !== 'Tab') return;
                      const activeId = event.currentTarget.getAttribute('aria-activedescendant');
                      const action = !event.shiftKey && activeId && document.getElementById(activeId)?.querySelector('[data-palette-actions] button:not(:disabled), [data-palette-actions] a[href]');
                      if (action) {
                        event.preventDefault();
                        event.stopPropagation();
                        action.focus();
                      } else if (!selectOnTab) {
                        // Keep native focus traversal without Combobox accepting a command.
                        event.stopPropagation();
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        onClose();
                        return;
                      }
                    }}
                  />
                  {mobileFullscreen && <Button variant="ghost" className="sm:hidden" onClick={onClose}>Cancel</Button>}
                  <span className={cn("text-[10px] text-bun-faint border border-bun-line rounded-[4px] px-[6px] py-px", mobileFullscreen && "hidden sm:inline")}>
                    esc
                  </span>
                </div>
                <Combobox.Options static className={cn('m-0 list-none p-[6px] overflow-y-auto', mobileFullscreen ? 'min-h-0 flex-1 pb-[env(safe-area-inset-bottom)] sm:max-h-[400px]' : mobileSheet ? 'min-h-0 max-h-[60dvh] sm:max-h-[400px]' : 'max-h-[400px]', footer && 'sm:max-h-[min(328px,55dvh)]')}>
                  {children}
                </Combobox.Options>
              </Combobox>
              {footer && <div className="flex shrink-0 flex-wrap items-center gap-x-[14px] gap-y-2 border-t border-bun-line-soft bg-bun-canvas px-[14px] py-2 text-bun-meta text-bun-stone">{footer}</div>}
            </Dialog.Panel>
          </Transition.Child>
        </div>
        {portal && <Portal>{portal}</Portal>}
      </Dialog>
    </Transition>
  );
}

// The controlled anchored panel stays open across drill-in selections. Open its
// combobox through the same public keyboard interaction used by its input.
function AnchoredResults({ resultsOpen, inputRef, anchorRef, portalTarget, keyboardTargetRef,
  inputLeading, onClose, query, onQueryChange, placeholder, ariaLabel, width, selectOnTab, children }) {
  const panelRef = useRef(null);
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const anchor = anchorRef?.current;
    return () => {
      if (panel?.contains(document.activeElement)) anchor?.focus({ preventScroll: true });
    };
  }, [anchorRef]);

  useEffect(() => {
    if (!resultsOpen) {
      inputRef.current?.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true, cancelable: true }));
    }
  }, [resultsOpen, inputRef]);

  useEffect(() => {
    const target = keyboardTargetRef?.current;
    if (!target) return undefined;
    const forwardKey = event => {
      if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab'].includes(event.key) || event.isComposing
        || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey || (event.key === 'Tab' && !selectOnTab)) return;
      event.preventDefault();
      event.stopPropagation();
      inputRef.current?.focus({ preventScroll: true });
      inputRef.current?.dispatchEvent(new KeyboardEvent('keydown', { key: event.key, bubbles: true, cancelable: true }));
    };
    target.addEventListener('keydown', forwardKey);
    return () => target.removeEventListener('keydown', forwardKey);
  }, [keyboardTargetRef, inputRef, selectOnTab]);

  return <FloatingPanel ref={panelRef} open autoFocus tabIndex={-1} className="outline-none"
    onFocus={event => { if (event.target === event.currentTarget) inputRef.current?.focus({ preventScroll: true }); }}
    anchorRef={anchorRef} portalTarget={portalTarget} align="start" width={width} onDismiss={onClose} aria-label={ariaLabel}>
    <div className="flex items-center gap-2 p-3">
      {inputLeading}
      {/* Returning to the composer is not a selection; Headless UI otherwise accepts on blur. */}
      <Combobox.Input ref={inputRef} onBlur={event => event.preventDefault()} aria-label={placeholder} placeholder={placeholder} value={query}
        className="min-w-0 flex-1 rounded-bun border border-bun-line bg-bun-surface px-3 py-2 text-bun-body text-bun-text placeholder:text-bun-faint focus:outline-none focus:ring-2 focus:ring-bun-blue"
        onChange={event => onQueryChange?.(event.target.value)}
        onKeyDownCapture={event => {
          if (event.key !== 'Tab') return;
          if (event.shiftKey || !selectOnTab) {
            event.stopPropagation();
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          const activeId = event.currentTarget.getAttribute('aria-activedescendant');
          if (activeId) document.getElementById(activeId)?.click();
        }}
        onKeyDown={event => {
          if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) event.stopPropagation();
        }} />
    </div>
    <Combobox.Options static aria-label={ariaLabel} className="m-0 max-h-[240px] list-none overflow-y-auto px-3 pb-3">
      {children}
    </Combobox.Options>
  </FloatingPanel>;
}

/** Caps section label between result groups ("Clients", "Actions"). */
function Group({ label, divided = false, children }) {
  return (
    <li className={cn('list-none', divided && 'mt-[6px] border-t border-bun-line-soft pt-1')}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-bun-faint px-[10px] pt-[6px] pb-1">
        {label}
      </div>
      <ul className="m-0 list-none p-0">{children}</ul>
    </li>
  );
}

/** One result row. `value` is what onSelect receives; `hint` sits far right. */
function Item({ value, icon, hint, actions, actionsOnActive = false, strong = false, variant, disabled, className, children }) {
  return (
    <Combobox.Option value={value} disabled={disabled} as={Fragment}>
      {({ active }) => (
        <li
          className={cn(
            "group flex flex-wrap min-h-11 sm:min-h-0 items-center text-bun-body cursor-pointer",
            variant === 'navigation' ? 'gap-x-[10px] gap-y-0 px-2 py-[7px] rounded-bun focus-within:bg-bun-mist' : 'gap-[10px] px-[10px] py-2 rounded-[8px]',
            active && (variant === 'navigation' ? 'bg-bun-mist' : 'bg-bun-wash'),
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          {icon}
          <span className={cn('flex-1 min-w-0 truncate', strong ? 'font-strong' : 'font-book')}>{children}</span>
          {hint && <span className="text-[10px] text-bun-faint whitespace-nowrap">{hint}</span>}
          {actions && <div data-palette-actions={actionsOnActive ? '' : undefined} className={cn('w-full', variant === 'navigation' ? 'pt-2 pb-[2px] pl-[38px] sm:w-auto sm:shrink-0 sm:p-0' : 'pl-8', actionsOnActive && !active && 'sm:hidden sm:group-focus-within:block')}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') event.stopPropagation(); }} onClick={(event) => event.stopPropagation()}>{actions}</div>}
        </li>
      )}
    </Combobox.Option>
  );
}

function Icon({ children }) {
  return <span aria-hidden="true" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-bun-sm bg-bun-mist text-bun-slate">{children}</span>;
}

function Footer({ count, enterLabel = 'open', children }) {
  return <><span className="hidden sm:inline"><Kbd subtle>↑↓</Kbd> navigate</span><span className="hidden sm:inline"><Kbd subtle>↵</Kbd> {enterLabel}</span>{children}<span className="ml-auto">{count}</span></>;
}

/** Highlighted match inside a result name. */
function Match({ children }) {
  return <span className="bg-bun-warning-bg rounded-[3px]">{children}</span>;
}

/** Muted trailing detail inside a result name ("· $1.24M"). */
function Meta({ children }) {
  return <span className="text-bun-label tracking-normal font-book text-bun-stone"> {children}</span>;
}

CommandPalette.Group = Group;
CommandPalette.Item = Item;
CommandPalette.Match = Match;
CommandPalette.Meta = Meta;
CommandPalette.Icon = Icon;
CommandPalette.Footer = Footer;
